/**
 * DESIGN.md §7.8 — Footer (Desktop).
 *
 * Dark-themed component (`.theme-dark` scope re-points heading-1 /
 * subheading-1 / border-secondary). Three sections:
 *
 *   1. Brand column: white logo + tagline (h6 regular) + the Start for free button
 *   2. Menu columns: Product / Integrations / Pricing / Docs / Company at 64px gap
 *      (spec 115 phase B — the footer is the information architecture)
 *   3. Legal bar: top border, ©<year> connect0.ai inc + Terms/Privacy
 *
 * Responsive (CON-1325): the five menu columns need 816px in a row
 * (5 × 112px + 4 × 64px), so the row beside the brand column starts at
 * `lg` (1280px in the marketing breakpoints, DESIGN.md §3.6). Below it
 * the brand sits above the menu, and the menu is a grid: 2 columns on a
 * phone, 3 from `md`. No width scrolls sideways (the e2e smoke checks
 * 390–1440px).
 */
import { NOUNS, TAGLINE } from '../copy/marketing-copy.js';
import { Bounded } from '../primitives/bounded.js';
import { Logo } from '../primitives/logo.js';
import { PillButton } from '../primitives/pill-button.js';
import { PlainTextLink } from '../primitives/plain-text-link.js';

interface FooterColumn {
  heading: string;
  links: ReadonlyArray<{ label: string; href: string; external?: boolean }>;
}

// Spec 115 phase B — the footer IS the information architecture:
// Product (the three nouns) · Pricing · Docs · Company (About / Open /
// Safety / Contact / Support / LinkedIn). `/open` is the footer's §1.4 proof surface.
/** The footer's one action (CON-954). Title case is the owner's choice for this button; the hero keeps sentence case. */
const FOOTER_CTA = { label: 'Start for free', href: '/start' } as const;

const COLUMNS: readonly FooterColumn[] = [
  {
    heading: 'Product',
    // The three nouns, then the company skills page (a capability, not a noun).
    links: [...NOUNS.map((n) => ({ label: n.noun, href: `/${n.slug}` })), { label: 'Company skills', href: '/skills' }],
  },
  {
    heading: 'Integrations',
    links: [{ label: 'MCP Servers', href: '/integrations/mcp' }],
  },
  {
    heading: 'Pricing',
    links: [
      { label: 'Pay per use', href: '/pricing' },
      { label: 'How pricing works', href: '/docs/pricing' },
    ],
  },
  {
    heading: 'Docs',
    links: [
      { label: 'Docs', href: '/docs' },
      { label: 'Guides', href: '/guides' },
      { label: 'Blog', href: '/blog' },
      { label: 'For agents', href: '/skill.md' },
    ],
  },
  {
    heading: 'Company',
    links: [
      { label: 'About', href: '/about' },
      { label: 'Open', href: '/open' },
      { label: 'Safety', href: '/safety' },
      { label: 'Contact', href: '/contact' },
      { label: 'Support', href: '/support' },
      { label: 'LinkedIn', href: 'https://linkedin.com/company/connect0-ai', external: true },
    ],
  },
];

export function MarketingFooter() {
  // Always render the current year. Server-rendered → resolves at
  // request time (or at build time for statically-generated routes,
  // which is good enough because the site is rebuilt on every deploy
  // and we cut releases well more than once a year).
  const year = new Date().getFullYear();
  return (
    // Full-bleed dark surface; content columns are bounded to the 1312px
    // "sticky-bg" width via <Bounded>.
    <footer className="theme-dark w-full bg-primary-jet-500">
      <Bounded className="flex flex-col gap-10 pt-14 pb-10 md:gap-14 md:pt-20 lg:gap-[72px] lg:pt-[88px]">
        <div className="flex flex-col gap-10 md:gap-14 lg:flex-row lg:items-start lg:justify-between lg:gap-10">
          <div className="flex flex-col gap-6 md:gap-14 md:w-[440px] md:max-w-full">
            <Logo variant="dark" className="h-[32px]" />
            {/* One line, once — the second ("The AI workforce platform.") was retired 2026-09-20 (CON-954). */}
            <p className="heading-6 text-text-heading-1">{TAGLINE}</p>
            {/* CON-954 — the footer's call to action is a button, not a
                link in the Pricing column: flame on jet (the accent
                scheme; the primary jet fill would vanish on this dark
                surface). Label casing is the owner's for the footer. */}
            <div>
              <PillButton size="lg" scheme="accent" href={FOOTER_CTA.href}>
                {FOOTER_CTA.label}
              </PillButton>
            </div>
          </div>
          <nav className="grid grid-cols-2 gap-x-2 gap-y-14 md:grid-cols-3 md:gap-x-8 lg:flex lg:gap-16">
            {COLUMNS.map((col) => (
              <div key={col.heading} className="flex min-w-0 flex-col gap-4 md:gap-6 lg:w-28">
                <span className="font-label font-medium text-label-md text-text-overline">{col.heading}</span>
                <ul className="flex flex-col gap-3 md:gap-4">
                  {col.links.map((link) => (
                    <li key={link.href}>
                      <PlainTextLink
                        href={link.href}
                        className="text-primary-white hover:text-primary-steel-200"
                        // Off-site links open in a new tab. `noreferrer` is
                        // paired with `noopener` deliberately: `noopener`
                        // alone still leaks the referrer, and there is no
                        // reason for a social profile to learn which page
                        // of ours the click came from.
                        {...(link.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                      >
                        {link.label}
                      </PlainTextLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </div>
      </Bounded>

      {/* Legal bar: the rule + its content are both bounded to the content
          column (the divider matches the content width, not full-bleed).
          Text lifted from jet-200 (#6C6C6C — ~2:1 on jet-500, fails AA) to
          steel-300 (#D9D9D8 — ~11:1) so the copyright + links are legible. */}
      <Bounded className="pb-8">
        <div className="flex items-center justify-between border-t border-border-secondary py-6">
          <span className="font-label text-label-sm text-primary-steel-300">© {year} connect0.ai inc</span>
          <div className="flex items-center gap-6">
            <PlainTextLink href="/terms" className="text-label-sm text-primary-steel-300 hover:text-primary-white">
              Terms
            </PlainTextLink>
            <PlainTextLink href="/privacy" className="text-label-sm text-primary-steel-300 hover:text-primary-white">
              Privacy
            </PlainTextLink>
          </div>
        </div>
      </Bounded>
    </footer>
  );
}
